import { withAuthenticationRequired } from '@tom-ui/utils'
import { Box } from '@mui/material'
import { CargoType, CarrierType, CarrierVisitDirection, OrderResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { useGeneralCargoLocations } from '@planning/hooks/useGeneralCargoLocations'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo } from 'react'
import { GeneralCargoOrderLocationItem } from '../GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { BaplieParseResult } from '../Order/stores/OrderListUploadParsingStore'
import { VisitGeneralCargoTable } from '../Visits/Components/organisms/VisitGeneralCargoTable'
import { ContainerYardOperationViewStore } from './Components'
import { VesselVisitOrderListHeader } from './Components/molecules/VesselVisitOrderListHeader'
import { VesselVisitContainerLoadTable } from './Components/organisms/VesselVisitTable/VesselVisitContainerLoadTable'
import { OrderListUploadDialogV2 } from './Components/Upload/OrderListUploadDialog'
import { VesselVisitLoadListViewStore } from './Stores/VesselVisitOrderListViewStore'

type Props = {
  vesselVisitItem: IVesselVisitItem
  containerYardOperationViewStore: ContainerYardOperationViewStore
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
  onOpenGeneralCargoReservation?: (order: OrderResponseDto) => void
  getOrderReservationsFunc?: (
    orderIds: number[],
  ) => Promise<Array<{ orderId: number; planned: GeneralCargoOrderLocationItem[] }>>
  reservationUpdate?: { orderId: number; reservations: GeneralCargoOrderLocationItem[] }
}

export const VesselVisitLoadListPage: FC<Props> = withAuthenticationRequired(
  observer(
    ({
      containerYardOperationViewStore,
      vesselVisitItem,
      parseBaplie,
      onOpenGeneralCargoReservation,
      getOrderReservationsFunc,
      reservationUpdate,
    }) => {
      const theme = useMinimalsTheme()

      const { orderListUploadViewStoreV2, weightClassItemStore } = usePlanningStore()

      useEffect(() => {
        weightClassItemStore.fetch()
      }, [weightClassItemStore])

      const vesselVisitLoadListViewStore = useMemo(() => {
        return new VesselVisitLoadListViewStore(vesselVisitItem)
      }, [vesselVisitItem])

      const onUploadClick = async () => {
        const visitId = vesselVisitItem?.id

        orderListUploadViewStoreV2.reset()

        await orderListUploadViewStoreV2.setVesselVisitId(visitId)
        await orderListUploadViewStoreV2.setDirection(CarrierVisitDirection.Outbound)

        orderListUploadViewStoreV2.setOpen(true)
      }

      const orders = useMemo(() => {
        return (
          vesselVisitLoadListViewStore.vesselVisitItem?.load?.generalCargoOrders.map(
            order => order.data,
          ) ?? []
        )
      }, [vesselVisitLoadListViewStore.vesselVisitItem?.load?.generalCargoOrders])

      const { orderReservationsById } = useGeneralCargoLocations(
        orders,
        undefined,
        getOrderReservationsFunc,
        reservationUpdate,
      )

      return (
        <>
          {vesselVisitItem && (
            <>
              <VesselVisitOrderListHeader
                vesselVisitItem={vesselVisitItem}
                direction='load'
                onUploadClick={onUploadClick}
              />
              <Box padding={theme.customSpacing.m}>
                {vesselVisitItem?.data.cargoType === CargoType.Container && (
                  <VesselVisitContainerLoadTable
                    vesselVisitLoadListViewStore={vesselVisitLoadListViewStore}
                    containerYardOperationViewStore={containerYardOperationViewStore}
                  />
                )}

                {vesselVisitItem?.data.cargoType === CargoType.GeneralCargo && (
                  <VisitGeneralCargoTable
                    orders={vesselVisitLoadListViewStore.vesselVisitItem?.load?.generalCargoOrders}
                    carrierVisit={vesselVisitItem}
                    carrierVisitType={CarrierType.Vessel}
                    direction={CarrierVisitDirection.Outbound}
                    onOpenGeneralCargoReservation={onOpenGeneralCargoReservation}
                    orderReservationsById={orderReservationsById}
                  />
                )}
              </Box>

              <OrderListUploadDialogV2
                enableFieldSelectionFromOrdersView={false}
                open={orderListUploadViewStoreV2.open}
                handleDialogClose={() => {
                  orderListUploadViewStoreV2.setOpen(false)
                }}
                visitType={CarrierType.Vessel}
                parseBaplie={parseBaplie}
              />
            </>
          )}
        </>
      )
    },
  ),
)
